import { useState, useEffect, useCallback, useReducer, useRef, useMemo } from 'react';
import type { EmailOtpType } from '@supabase/supabase-js';
import { useAuth } from './contexts/AuthContext';
import { supabase } from './lib/supabase';
import { usePaymentStatus } from './hooks/usePaymentStatus';
import { useRecentEquipment } from './hooks/useRecentEquipment';
import { Project, Equipment, AMIDataPoint, ProjectStatus } from './types/project';
import { ProjectInfoForm } from './components/ProjectInfoForm';
import { ServiceInfoForm } from './components/ServiceInfoForm';
import { EquipmentSchedule } from './components/EquipmentSchedule';
import { ExistingLoadStep } from './components/ExistingLoadStep';
import { proposedLoads, enteredProposedLoads, existingLoads, removedLoads, enteredRemovedLoads } from './lib/calc/loadRoles';
import { citationsFor } from './lib/calc/citations';
import { resolvedEditionKey } from './lib/calc/editions';
import { toEquipmentInsertRows } from './lib/equipmentPayload';
import { canRunTraditional, runTraditionalCalc } from './lib/calc/runTraditional';
import { ResultsView } from './components/ResultsView';
import ProjectManager from './components/ProjectManager';
import { ProjectNameDialog } from './components/ProjectNameDialog';
import { WelcomeModal } from './components/WelcomeModal';
import { UnlockProjectDialog } from './components/UnlockProjectDialog';
import { LoginForm } from './components/LoginForm';
import { SignupForm } from './components/SignupForm';
import { ForgotPasswordForm } from './components/ForgotPasswordForm';
import { ResetPasswordForm } from './components/ResetPasswordForm';
import { AccountSettings, type AccountSettingsTab } from './components/AccountSettings';
import { BillingConsentModal } from './components/BillingConsentModal';
import AdminDashboard from './components/admin/AdminDashboard';
import FirmAdminDashboard from './components/FirmAdminDashboard';
import { firmLogoForReport } from './lib/firmLogo';
import CompletionValidationModal from './components/CompletionValidationModal';
import {
  validateProjectForCompletion,
  saveValidationSnapshot,
} from './validation/projectCompletionValidation';
import type { ValidationResult } from './validation/projectCompletionValidation';
import { calculateNEC22087, determineSeason } from './lib/calculations';
import { calcRulesFor } from './lib/calc/rulesVersion';
import { putAmi, getAmi, deleteAmi } from './lib/amiStore';
import { fetchAllAmiPoints } from './lib/amiFetch';
import { summarizeAmiSeries } from './lib/amiSummary';
import { persistAmiIntervals, deleteAmiIntervals } from './lib/amiPersistence';
import AmiPersistStatus from './components/AmiPersistStatus';
import MfaChallengeScreen from './components/mfa/MfaChallengeScreen';
import MfaEnrollmentScreen from './components/mfa/MfaEnrollmentScreen';
import { toSupabasePayload, validatePayloadSchema } from './lib/projectPayload';
import { isResultsUnlocked } from './lib/paywall';
import { enterpriseTotal } from './lib/enterprisePricing';
import { preparerDefaults, blankPreparerDefaults } from './lib/preparerDefaults';
import { ProjectProgressBar } from './components/ProjectProgressBar';
import { useProjectProgress } from './hooks/useProjectProgress';
import { DemoOverlay } from './components/onboarding/DemoOverlay';
import type { DemoSeed, DemoState } from './components/onboarding/demoScript';
import { isTourAutoOfferEligible } from './lib/onboarding';
import {
  buildDemoAmiSeries,
  buildDemoProject,
  demoAmiFields,
  DEMO_PREPARER,
  isDemoProjectId,
  materializeDemoEquipment,
  materializeDemoInventory,
  DEMO_PROJECT_ID,
} from './lib/demoProject';
import {
  saveProjectLocally,
  loadProjectLocally,
  deleteLocalProject,
  generateLocalProjectId,
  setCurrentProjectId,
  migrateOldLocalStorage,
  sanitizeLocalProjects,
  clearAllLocalProjects,
  getAllLocalProjects,
  getLastProjectId,
  setLastProjectId,
  clearLastProjectId,
  purgeOrphanedLocalProjects,
} from './lib/localProjects';
import { FileText, Zap, ClipboardList, Database, BarChart3, FolderOpen, Save, Settings, Lock, Unlock, ShieldCheck, HelpCircle, AlertCircle, CheckCircle, Building2, Users, Sparkles } from 'lucide-react';
import { HelpTab } from './components/HelpTab';
import { readingsInEffect } from './lib/peakExclusions';

// The demo's sample job, split by role. Its helpers work on one role at a
// time — "the last row" means the last PROPOSED row, and the traditional
// path's inventory must survive every rewrite of the new loads.
const splitDemoRows = (rows: Equipment[]) => ({
  proposed: proposedLoads(rows),
  existing: existingLoads(rows),
});

type Tab = 'project' | 'service' | 'equipment' | 'ami' | 'results' | 'help';

/**
 * Inert callback for firm view-only mode.
 *
 * ProjectInfoForm / ServiceInfoForm / AMIDataInput / EquipmentSchedule declare
 * their mutation handlers as required props and invoke them unguarded, but a
 * firm member with view-only permission must not be able to mutate the project.
 * Passing `undefined` used to satisfy that at runtime only because those
 * components render every control disabled when `isReadOnly` is true, so the
 * handler is never reached — but it does not type-check. Passing this no-op
 * instead is observationally identical (an `onClick={undefined}` button and an
 * `onClick={noop}` button both do nothing) while being type-correct.
 *
 * Module-scoped so its identity is stable across renders.
 */
const noopHandler = () => {};

interface ProjectState {
  projectId: string | null;
  project: Partial<Project>;
  equipment: Equipment[];
  amiData: AMIDataPoint[];
  isLoading: boolean;
}

type ProjectAction =
  | { type: 'LOAD_PROJECT'; payload: { projectId: string; project: Partial<Project>; equipment: Equipment[]; amiData: AMIDataPoint[] } }
  | { type: 'UPDATE_PROJECT'; payload: Partial<Project> }
  | { type: 'UPDATE_EQUIPMENT'; payload: Equipment[] }
  // Row-level actions apply to the CURRENT list, not the one a handler closed
  // over. With two schedules on the New Loads tab (new and removed), both
  // auto-fill effects fire in one commit; whole-array writes built from the
  // same render's `equipment` overwrote each other, and the loser's change
  // vanished (2026-10-13: a heat pump lost its concurrency answer, and a
  // removed row could fall back to its old role).
  | { type: 'PATCH_EQUIPMENT'; payload: { id: string; updates: Partial<Equipment> } }
  | { type: 'ADD_EQUIPMENT'; payload: Equipment }
  | { type: 'REMOVE_EQUIPMENT'; payload: string }
  | { type: 'UPDATE_AMI_DATA'; payload: AMIDataPoint[] }
  | { type: 'SET_PROJECT_ID'; payload: string }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'CLEAR_PROJECT' };

const initialProjectState: ProjectState = {
  projectId: null,
  project: {
    project_name: 'Untitled Project',
    status: 'draft',
    // No rating until the contractor enters one. A pre-filled 200 A satisfied
    // the "rating required" check on its own, so a project whose Service Info
    // tab was never opened printed a 200 A service on the report — on a
    // 100 A house that doubles the capacity the determination rests on
    // (audit 2026-09-02, S5). The Results tab stays disabled until entered.
    service_rating_amps: 0,
    service_voltage_numeric: 240,
    service_voltage_text: '120/240V single-phase',
    service_phase: 'single',
    service_type: 'unknown',
    installation_status: 'existing',
    methodology_version: 'NEC 220.87 v1.0',
  },
  equipment: [],
  amiData: [],
  isLoading: false,
};

function projectReducer(state: ProjectState, action: ProjectAction): ProjectState {
  switch (action.type) {
    case 'LOAD_PROJECT':
      return {
        ...state,
        projectId: action.payload.projectId,
        project: action.payload.project,
        equipment: action.payload.equipment,
        amiData: action.payload.amiData,
      };
    case 'UPDATE_PROJECT':
      return {
        ...state,
        project: { ...state.project, ...action.payload },
      };
    case 'UPDATE_EQUIPMENT':
      return {
        ...state,
        equipment: action.payload,
      };
    case 'PATCH_EQUIPMENT':
      return {
        ...state,
        equipment: state.equipment.map((eq) =>
          eq.id === action.payload.id ? { ...eq, ...action.payload.updates } : eq
        ),
      };
    case 'ADD_EQUIPMENT':
      return {
        ...state,
        equipment: [
          ...state.equipment,
          { ...action.payload, sort_order: state.equipment.length },
        ],
      };
    case 'REMOVE_EQUIPMENT':
      return {
        ...state,
        equipment: state.equipment.filter((eq) => eq.id !== action.payload),
      };
    case 'UPDATE_AMI_DATA':
      return {
        ...state,
        amiData: action.payload,
      };
    case 'SET_PROJECT_ID':
      return {
        ...state,
        projectId: action.payload,
      };
    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.payload,
      };
    case 'CLEAR_PROJECT':
      return {
        ...initialProjectState,
        projectId: null,
      };
    default:
      return state;
  }
}

type AuthView = 'login' | 'signup' | 'forgot-password' | 'reset-password';

function App() {
  const { user, userProfile, companyProfile, loading: authLoading, bootstrapError, bootstrapErrorDetail, retryBootstrap, isAdmin, isDisabled, isPasswordRecovery, mfaGate, signOut, refreshProfile, setOnboardingStatus, confirmEmail } = useAuth();
  const [activeTab, setActiveTab] = useState<Tab>('project');
  const [projectState, dispatch] = useReducer(projectReducer, initialProjectState);
  const [loading, setLoading] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [showProjectManager, setShowProjectManager] = useState(false);
  const [showProjectNameDialog, setShowProjectNameDialog] = useState(false);
  const [showUnlockDialog, setShowUnlockDialog] = useState(false);
  const [showAccountSettings, setShowAccountSettings] = useState(false);
  const [accountSettingsTab, setAccountSettingsTab] = useState<AccountSettingsTab>('profile');
  const [showAdminDashboard, setShowAdminDashboard] = useState(false);
  const [showFirmDashboard, setShowFirmDashboard] = useState(false);
  const [firmInvite, setFirmInvite] = useState<{ firm_name: string; invited_email: string } | null>(null);
  const [firmName, setFirmName] = useState<string | null>(null);
  // Firm logo: the public URL for the header, and a self-contained image
  // source for the report letterhead. Both null for individual accounts.
  const [firmLogoUrl, setFirmLogoUrl] = useState<string | null>(null);
  const [firmLogoSrc, setFirmLogoSrc] = useState<string | null>(null);
  const [firmProjectOwnerName, setFirmProjectOwnerName] = useState<string | null>(null);
  const [firmProjectEditPermission, setFirmProjectEditPermission] = useState<string>('own_only');
  const [authView, setAuthView] = useState<AuthView>('login');
  const [authNotice, setAuthNotice] = useState<{ type: 'success' | 'error'; message: string; otpType: EmailOtpType } | null>(null);
  const [lastSaveTime, setLastSaveTime] = useState<Date | null>(null);
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [validationResult, setValidationResult] = useState<ValidationResult | null>(null);
  const [showRequiredHighlights, setShowRequiredHighlights] = useState(false);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  // Guided demo. Runs on a throwaway sample project; `demoSeed` is non-null for
  // exactly as long as it is playing.
  const [demoSeed, setDemoSeed] = useState<DemoSeed | null>(null);
  // The user's real project state, parked while the demo borrows the screen.
  const preDemoRef = useRef<{
    projectId: string | null;
    project: Partial<Project>;
    equipment: Equipment[];
    amiData: AMIDataPoint[];
  } | null>(null);
  const [autoSaveError, setAutoSaveError] = useState<string | null>(null);
  const [paywallToast, setPaywallToast] = useState<string | null>(null);
  const [enterpriseSuccessBanner, setEnterpriseSuccessBanner] = useState(false);
  const [paymentStatusKey, setPaymentStatusKey] = useState(0);
  const [pendingCheckout, setPendingCheckout] = useState<
    | { kind: 'unlimited' }
    | {
        kind: 'enterprise';
        company_name: string;
        seat_count: number;
        billing_cycle: 'monthly' | 'annual';
        take_seat: boolean;
      }
    | null
  >(null);

  const { projectId, project, equipment, amiData, isLoading } = projectState;

  const isReadOnly = project.status === 'completed';
  // The readings the calculation uses: every reading less the windows the
  // contractor excluded (lib/peakExclusions.ts). Raw `amiData` is what is
  // stored and persisted; everything that computes or shows the peak reads
  // this instead.
  const amiInEffect = useMemo(
    () => readingsInEffect(amiData, project),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [amiData, project.ami_exclusions, project.calc_rules_version, project.status, project.completed_at]
  );
  // True only when viewing another member's project with view-only firm permission.
  // Members with firm_all edit permission behave identically to owners.
  const isFirmViewOnly = !!(
    project.user_id &&
    user?.id &&
    project.user_id !== user.id &&
    firmProjectEditPermission === 'own_only'
  );
  const isPasswordReset = isPasswordRecovery || window.location.hash.includes('type=recovery');

  // Live completion progress for the strip and the tab dots.
  // Full equipment list: the completion validator is report-basis-aware and
  // splits proposed rows from dwelling-inventory rows itself.
  const progress = useProjectProgress(projectId, project, equipment, amiInEffect);

  // Which sub-path of the Existing Load tab is open. Held here so leaving the
  // tab and coming back does not forget an explicit choice (the step itself
  // unmounts on tab switches); cleared when the project changes.
  const [existingLoadMode, setExistingLoadMode] = useState<'measured' | 'traditional' | null>(null);
  useEffect(() => {
    setExistingLoadMode(null);
  }, [projectId]);

  // THE demo guard. Every write path below consults this: the sample project is
  // fabricated customer data on a tool that prints permit documents, so it must
  // never reach localStorage, IndexedDB, or Supabase.
  const inDemo = isDemoProjectId(projectId);

  // Fresh equipment for the demo's callbacks. The demo adds a row and then
  // fills it within one beat, so a closure captured before the add would map
  // over the old, empty array.
  const equipmentRef = useRef<Equipment[]>(equipment);
  useEffect(() => { equipmentRef.current = equipment; }, [equipment]);

  // Compacts the logo once the page moves, so the locked header + tabs +
  // progress strip stay a reasonable share of a small screen. The hook carries
  // the hysteresis and short-page guard that keep it from flickering.
  // Quick-add on New Loads: the contractor's own recent entries. Off in the
  // demo, which would otherwise mix a real history into the sample job.
  const { items: recentEquipment, remove: removeRecentEquipment } =
    useRecentEquipment(user?.id, equipment, !!user && !inDemo);

  const paymentStatus = usePaymentStatus(user ? paymentStatusKey + 1 : 0);

  // Track whether we arrived from a ?unlock=success redirect
  const unlockSuccessRef = useRef(false);
  const didRefetchUnlockRef = useRef(false);

  // Detect ?unlock=success on mount — strip params, show toast, navigate to Results
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('unlock') !== 'success') return;
    unlockSuccessRef.current = true;

    window.history.replaceState({}, '', window.location.pathname + window.location.hash);
    setPaywallToast('Project unlocked. Results are permanently available.');
    setTimeout(() => setPaywallToast(null), 5000);
    setActiveTab('results');
    setPaymentStatusKey(k => k + 1);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Detect ?enterprise=success on mount — show banner, strip param
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('enterprise') === 'success') {
      setEnterpriseSuccessBanner(true);
      params.delete('enterprise');
      const newSearch = params.toString();
      const newUrl = window.location.pathname + (newSearch ? `?${newSearch}` : '') + window.location.hash;
      window.history.replaceState({}, '', newUrl);
    }
  }, []);

  // Detect ?token_hash=&type= on mount — our own same-domain link (see
  // AuthContext.confirmEmail), used for both signup confirmation and password
  // recovery. Verifying here, on our domain, is what lets the email's visible
  // link match the sending domain instead of pointing straight at the raw
  // Supabase project host, which corporate mail filters read as a phishing
  // signal and quarantine even though Resend reports it delivered. A
  // successful 'recovery' verify fires PASSWORD_RECOVERY (AuthContext already
  // listens for it), which routes straight to ResetPasswordForm before this
  // component ever renders LoginForm — so only the error copy below needs to
  // account for both flows; the success copy is signup-only in practice.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tokenHash = params.get('token_hash');
    const type = params.get('type');
    if (!tokenHash || !type) return;
    params.delete('token_hash');
    params.delete('type');
    const newSearch = params.toString();
    const newUrl = window.location.pathname + (newSearch ? `?${newSearch}` : '') + window.location.hash;
    window.history.replaceState({}, '', newUrl);
    const otpType = type as EmailOtpType;
    confirmEmail(tokenHash, otpType).then(({ error }) => {
      if (error) {
        const msg = error.message.toLowerCase();
        const stale = msg.includes('expired') || msg.includes('invalid') || msg.includes('already');
        const message =
          otpType === 'recovery'
            ? stale
              ? 'This password reset link has expired or was already used. Click "Forgot password?" below to request a new one.'
              : 'Could not verify that reset link. Click "Forgot password?" below to request a new one.'
            : otpType === 'signup'
              ? stale
                ? 'This confirmation link has expired or was already used. Sign in, or request a new one below.'
                : 'Could not confirm your email. Please sign in, or request a new confirmation email below.'
              : 'This link has expired or was already used. Please try again or contact support@ethermsolutions.com.';
        setAuthNotice({ type: 'error', message, otpType });
      } else {
        setAuthNotice({ type: 'success', message: 'Email confirmed! You can now sign in.', otpType });
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Detect ?join=<token> on mount — stash token, strip URL, fetch firm info for branded auth screens
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const joinToken = params.get('join');
    if (joinToken) {
      localStorage.setItem('trueload_pending_join', joinToken);
      params.delete('join');
      const newSearch = params.toString();
      const newUrl = window.location.pathname + (newSearch ? `?${newSearch}` : '') + window.location.hash;
      window.history.replaceState({}, '', newUrl);
      fetch(`/.netlify/functions/get-invite-info?token=${encodeURIComponent(joinToken)}`)
        .then(r => r.json())
        .then(data => {
          if (data.valid) setFirmInvite({ firm_name: data.firm_name, invited_email: data.invited_email });
        })
        .catch(() => {});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Once projectId is available after a checkout redirect, re-fetch results_unlocked from Supabase
  useEffect(() => {
    if (!unlockSuccessRef.current || !projectId || projectId.startsWith('local_') || didRefetchUnlockRef.current) return;
    didRefetchUnlockRef.current = true;
    supabase
      .from('projects')
      .select('results_unlocked, results_unlocked_at')
      .eq('id', projectId)
      .single()
      .then(({ data }) => {
        if (data) dispatch({ type: 'UPDATE_PROJECT', payload: data });
      });
  }, [projectId]);

  // Track the last user ID we ran loadInitialProject for so we can detect
  // real user changes (login / logout / account switch) after initial load.
  const lastLoadedUserIdRef = useRef<string | null | undefined>(undefined);
  // Mirrors projectState.projectId so async flows (loadInitialProject) can
  // detect that a project became active while they were in flight.
  const activeProjectIdRef = useRef<string | null>(null);
  useEffect(() => { activeProjectIdRef.current = projectId; }, [projectId]);
  const isPromotingRef = useRef(false);
  const pendingPlanHandledRef = useRef(false);
  const pendingJoinHandledRef = useRef(false);
  // Projects whose blank Preparer Information has already been seeded this
  // session. Keyed by project id so switching tabs or reloading a project does
  // not re-fill a field the user deliberately cleared.
  const backfilledPreparerRef = useRef<Set<string>>(new Set());

  // Preparer Information is owned by the Company Profile. New projects are
  // seeded at creation; this covers projects that predate the profile (or the
  // feature) by filling only the fields still blank, report date included.
  useEffect(() => {
    // Wait for the bootstrap to settle — until then a missing companyProfile
    // means "not loaded yet", not "no profile to draw on".
    if (authLoading || !projectId || !user) return;
    if (project.status === 'completed' || isFirmViewOnly) return;
    // The demo has a beat that shows the preparer block filling in from the
    // Company Profile; backfilling it here first would spoil that.
    if (isDemoProjectId(projectId)) return;
    if (backfilledPreparerRef.current.has(projectId)) return;

    const updates = blankPreparerDefaults(project, companyProfile, project.created_at);
    backfilledPreparerRef.current.add(projectId);
    if (Object.keys(updates).length > 0) {
      dispatch({ type: 'UPDATE_PROJECT', payload: updates });
    }
  }, [authLoading, projectId, companyProfile, user, project, isFirmViewOnly]);

  useEffect(() => {
    (window as any).clearLocalProjects = clearAllLocalProjects;
    return () => {
      delete (window as any).clearLocalProjects;
    };
  }, []);

  useEffect(() => {
    validatePayloadSchema(supabase);
  }, []);

  useEffect(() => {
    const handler = () => setActiveTab('help');
    window.addEventListener('trueload:navigate-help', handler);
    return () => window.removeEventListener('trueload:navigate-help', handler);
  }, []);

  useEffect(() => {
    if (authLoading) return;

    // Nothing may touch project data until the MFA gate is RESOLVED OPEN.
    // While it is 'pending', 'challenge', or 'enroll' the session can be aal1
    // with a verified factor, where the restrictive MFA policies filter every
    // row to zero WITHOUT an error — indistinguishable from an empty account.
    // loadInitialProject's orphan sweep would read that as "no cloud projects"
    // and purge every synced local copy plus its cached AMI intervals. The
    // guard sits above the dedupe ref on purpose: the ref must stay unset so
    // this re-runs for real once the gate opens.
    if (user && mfaGate !== 'none') return;

    const userId = user?.id ?? null;

    // Deduplicate: skip if same user as last run.
    if (lastLoadedUserIdRef.current === userId) return;
    lastLoadedUserIdRef.current = userId;

    if (user) {
      loadInitialProject().catch(console.error);
      // Non-blocking last_active_at ping
      supabase.from('user_profiles').update({ last_active_at: new Date().toISOString() }).eq('id', user.id).then(() => {});
    } else {
      // User logged out — clear project state so the next login starts clean.
      dispatch({ type: 'CLEAR_PROJECT' });
      setDemoSeed(null);
      preDemoRef.current = null;
    }
  // loadInitialProject is stable within the render; eslint can't verify that.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user?.id, mfaGate]);

  useEffect(() => {
    if (authLoading || !user) return;
    // Same gate rule as the project loader: a pending/closed MFA gate means
    // this session cannot yet act. This effect CONSUMES the stored pending
    // plan (removes it from localStorage), so running it behind the MFA
    // screens would silently discard a checkout the user is owed.
    if (mfaGate !== 'none') return;
    if (pendingPlanHandledRef.current) return;
    pendingPlanHandledRef.current = true;

    const rawPlan = localStorage.getItem('trueload_pending_plan');
    const rawEnterprise = localStorage.getItem('trueload_pending_enterprise');
    localStorage.removeItem('trueload_pending_plan');
    localStorage.removeItem('trueload_pending_enterprise');

    if (rawPlan) {
      try {
        const parsed = JSON.parse(rawPlan);
        // usePaymentStatus types subscription_status as 'active' | 'canceled' |
        // 'past_due' | null, but that union is incomplete: the Stripe webhook
        // (netlify/functions/stripe-webhook.js) also writes 'inactive', and the
        // hook passes the DB value straight through without validating it.
        // Widen to the real runtime type so the 'inactive' check below stays
        // live instead of being flagged as a non-overlapping comparison.
        const status: string | null = paymentStatus.subscription_status;
        if (parsed.plan === 'unlimited' && (status === null || status === 'inactive' || status === 'canceled')) {
          setPendingCheckout({ kind: 'unlimited' });
        }
      } catch {
        // silently fail — user lands in app normally
      }
      return;
    }

    if (rawEnterprise) {
      try {
        const parsed = JSON.parse(rawEnterprise);
        const { company_name, seat_count, billing_cycle, take_seat } = parsed;
        setPendingCheckout({
          kind: 'enterprise',
          company_name,
          seat_count,
          billing_cycle,
          // Absent for anyone who signed up before 3b and has a stash sitting
          // in localStorage; the old behaviour was always to take a seat.
          take_seat: take_seat !== false,
        });
      } catch {
        // silently fail — user lands in app normally
      }
      return;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, authLoading, paymentStatus.subscription_status]);

  useEffect(() => {
    if (authLoading || !user) return;
    if (pendingJoinHandledRef.current) return;
    pendingJoinHandledRef.current = true;

    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) return;
        const res = await fetch('/.netlify/functions/accept-firm-invite', {
          method: 'POST',
          headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({}),
        });
        if (!res.ok) return;
        const data = await res.json().catch(() => ({}));
        localStorage.removeItem('trueload_pending_join');
        if (data.success && data.firm_id) {
          await refreshProfile();
          setPaymentStatusKey(k => k + 1);
          if (data.firm_name) setFirmName(data.firm_name);
        }
      } catch (err) {
        console.error('auto-accept firm invite error:', err);
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, authLoading]);

  // Firm identity for the header badge, the header logo and the report
  // letterhead. Re-read each time the Firm Dashboard closes — that is the one
  // place the name or logo changes, and the header must not show yesterday's.
  useEffect(() => {
    if (!userProfile?.firm_id) {
      setFirmLogoUrl(null);
      return;
    }
    if (showFirmDashboard) return;
    let cancelled = false;
    supabase.from('firms').select('name, logo_url').eq('id', userProfile.firm_id).single()
      .then(({ data }) => {
        if (cancelled || !data) return;
        setFirmName(data.name);
        setFirmLogoUrl(data.logo_url ?? null);
      });
    return () => { cancelled = true; };
  }, [userProfile?.firm_id, showFirmDashboard]);

  // The report embeds the logo rather than linking to it, so fetch it once
  // per change and keep the embeddable form beside the URL.
  useEffect(() => {
    if (!firmLogoUrl) {
      setFirmLogoSrc(null);
      return;
    }
    let cancelled = false;
    firmLogoForReport(firmLogoUrl).then(src => { if (!cancelled) setFirmLogoSrc(src); });
    return () => { cancelled = true; };
  }, [firmLogoUrl]);

  useEffect(() => {
    const handleProjectNameUpdate = (event: CustomEvent) => {
      const { projectId: updatedProjectId, projectName } = event.detail;
      if (projectId === updatedProjectId) {
        dispatch({ type: 'UPDATE_PROJECT', payload: { project_name: projectName } });
      }
    };

    window.addEventListener('projectNameUpdated', handleProjectNameUpdate as EventListener);

    return () => {
      window.removeEventListener('projectNameUpdated', handleProjectNameUpdate as EventListener);
    };
  }, [projectId]);

  useEffect(() => {
    // Demo guard: the sample project is never written to this browser.
    if (!projectId || isLoading || isDemoProjectId(projectId)) {
      return;
    }

    const timeoutId = setTimeout(() => {
      saveProjectLocally(projectId, project, equipment, amiData, user?.id ?? undefined).catch(console.error);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [projectId, project, equipment, amiData, isLoading]);

  useEffect(() => {
    if (!projectId || isDemoProjectId(projectId)) return;

    const autoSaveInterval = setInterval(() => {
      autoSave();
    }, 30000);

    return () => clearInterval(autoSaveInterval);
  }, [projectId]);

  const handleActiveProjectDeleted = useCallback(async () => {
    if (user?.id && projectId) clearLastProjectId(user.id, projectId);
    if (projectId) await deleteLocalProject(projectId);
    dispatch({ type: 'CLEAR_PROJECT' });
  }, [user?.id, projectId]);

  const autoSave = useCallback(async () => {
    if (!projectId || isDemoProjectId(projectId)) return;
    // Do not write back to Supabase for projects we can only view
    if (
      projectState.project.user_id &&
      user?.id &&
      projectState.project.user_id !== user.id &&
      firmProjectEditPermission === 'own_only'
    ) return;

    try {
      const currentProjects = await getAllLocalProjects();
      const currentProject = currentProjects[projectId];

      if (!currentProject) return;

      // CRITICAL: Never autosave completed or archived projects
      if (currentProject.project.status === 'completed' || currentProject.project.status === 'archived') {
        console.warn(`Autosave skipped: Project ${projectId} is ${currentProject.project.status}. Use explicit unlock to modify.`);
        return;
      }

      if (user) {
        const now = new Date().toISOString();

        if (projectId.startsWith('local_')) {
          if (isPromotingRef.current) return;
          isPromotingRef.current = true;
          try {
            // Promote: INSERT a new cloud record and swap the local_ ID for the Supabase UUID
            const { data: newProject, error: insertError } = await supabase
              .from('projects')
              .insert({
                ...toSupabasePayload(currentProject.project as Project),
                user_id: user.id,
                last_opened_at: now,
              })
              .select()
              .single();

            if (insertError) throw insertError;

            dispatch({ type: 'SET_PROJECT_ID', payload: newProject.id });
            await deleteLocalProject(projectId);
            if (user.id) setLastProjectId(user.id, newProject.id);

            const oldAmi = await getAmi(projectId);
            if (oldAmi) {
              await putAmi(newProject.id, oldAmi);
              await deleteAmi(projectId);
            }

            if (currentProject.equipment.length > 0) {
              const { error: equipmentError } = await supabase
                .from('equipment')
                .insert(toEquipmentInsertRows(currentProject.equipment, newProject.id));
              if (equipmentError) throw equipmentError;
            }

            // Persist interval rows to the new UUID. AMI was just migrated into
            // IDB under newProject.id above, so read it back and route it through
            // the single gateway. Self-guards on empty/local; never throws.
            const promotedAmi = await getAmi(newProject.id);
            const amiPersisted = await persistAmiIntervals(newProject.id, promotedAmi ?? [], 'autosave-promote');
            console.log(
              `[promote] AMI persist for ${newProject.id}: state=${amiPersisted.state} ` +
              `sent=${amiPersisted.sent} inserted=${amiPersisted.inserted}` +
              (amiPersisted.error ? ` error=${amiPersisted.error}` : '')
            );
          } finally {
            isPromotingRef.current = false;
          }
        } else {
          // UPDATE existing cloud record
          const { error: updateError } = await supabase
            .from('projects')
            .update({
              ...toSupabasePayload(currentProject.project as Project),
              updated_at: now,
              last_opened_at: now,
            })
            .eq('id', projectId);

          if (updateError) throw updateError;

          const { error: deleteEqError } = await supabase.from('equipment').delete().eq('project_id', projectId);
          if (deleteEqError) throw deleteEqError;

          if (currentProject.equipment.length > 0) {
            // Autosave rows come from local storage and can predate the role
            // fields entirely — the gateway stamps them (equipmentPayload.ts).
            const { error: insertEqError } = await supabase
              .from('equipment')
              .insert(toEquipmentInsertRows(currentProject.equipment, projectId));
            if (insertEqError) throw insertEqError;
          }

          // AMI interval rows are no longer written here. Autosave read
          // currentProject from getAllLocalProjects(), where saveProjectLocally
          // hardcodes amiData: [] — so this path could never persist rows. AMI
          // now persists at the moments it actually changes (promote + upload)
          // via persistAmiIntervals().
        }
      }

      setLastSaveTime(new Date());
    } catch (error) {
      console.error('Auto-save error:', error);
      setAutoSaveError('Cloud save failed — please save manually.');
      setTimeout(() => setAutoSaveError(null), 5000);
    }
  }, [projectId]);

  async function loadInitialProject() {
    if (activeProjectIdRef.current) return;
    await migrateOldLocalStorage();
    await sanitizeLocalProjects();

    // Get userId from cached session — no network call.
    const { data: { session } } = await supabase.auth.getSession();
    const uid = session?.user?.id ?? null;

    // Sweep browser copies of projects that were deleted elsewhere, BEFORE any
    // tryLoad() runs. Otherwise a ghost loads as if real and the AMI backfill
    // fires the project-gone banner against it, once per reload.
    //
    // No .eq('user_id') filter: RLS already scopes this, and filtering would
    // purge firm projects the user can legitimately see. The id list must be
    // COMPLETE — a partial list makes live projects look orphaned — so every
    // page must succeed before purging. See the doc comment on
    // purgeOrphanedLocalProjects.
    try {
      if (uid) {
        const cloudIds = new Set<string>();
        const PAGE_SIZE = 1000;
        let from = 0;
        let fetchedAll = true;
        while (true) {
          const { data, error } = await supabase
            .from('projects')
            .select('id')
            .range(from, from + PAGE_SIZE - 1);
          if (error) { fetchedAll = false; break; }
          if (!data) { fetchedAll = false; break; }
          for (const row of data) cloudIds.add(row.id);
          if (data.length < PAGE_SIZE) break;
          from += PAGE_SIZE;
        }
        if (fetchedAll) {
          await purgeOrphanedLocalProjects(cloudIds);
        } else {
          console.warn('[orphanSweep] cloud project fetch incomplete — skipping purge');
        }
      }
    } catch (error) {
      // Never block startup on the sweep — the user still gets their project.
      console.error('[orphanSweep] failed:', error);
    }

    // Helper: load a local project into state (draft/in-progress only).
    // Updates the last-project pointer and returns true on success.
    async function tryLoad(id: string): Promise<boolean> {
      const localProject = await loadProjectLocally(id);
      if (!localProject) return false;
      const s = localProject.project.status;
      if (s !== 'draft' && s !== 'in-progress') return false;
      let amiData: AMIDataPoint[] = await getAmi(id) ?? [];
      if (amiData.length === 0 && !id.startsWith('local_')) {
        try { amiData = await fetchAndCacheAmiFromSupabase(id); } catch { /* non-critical */ }
      }
      // GUARD: user created/loaded a project while initial load was in flight — stand down.
      if (activeProjectIdRef.current) return true;
      dispatch({
        type: 'LOAD_PROJECT',
        payload: { projectId: localProject.projectId, project: localProject.project, equipment: localProject.equipment, amiData },
      });
      if (uid) setLastProjectId(uid, id);
      backfillAmiIfTableEmpty(id, amiData).catch((e) => console.error('[amiBackfill] launch failed:', e));
      return true;
    }

    // Step 1: restore the last explicitly opened project.
    if (uid) {
      const lastId = getLastProjectId(uid);
      if (lastId && await tryLoad(lastId)) return;
    }

    // Step 2: fall back to the most recently saved draft/in-progress local project.
    const localProjects = await getAllLocalProjects();
    const eligible = Object.values(localProjects)
      .filter(p => p.project.status === 'draft' || p.project.status === 'in-progress')
      .sort((a, b) => new Date(b.savedAt).getTime() - new Date(a.savedAt).getTime());

    for (const candidate of eligible) {
      if (await tryLoad(candidate.projectId)) return;
    }

    // Step 3: no eligible local projects — try to load the most recently
    // modified draft/in-progress project from the cloud directly.
    // This handles a user's first load on a new device.
    if (user) {
      try {
        const { data: candidates } = await supabase
          .from('projects')
          .select('id')
          .eq('user_id', user.id)
          .in('status', ['draft', 'in-progress'])
          .order('updated_at', { ascending: false })
          .limit(1);

        if (candidates && candidates.length > 0) {
          const cloudId = candidates[0].id;
          const { data: projectData, error: pErr } = await supabase
            .from('projects').select('*').eq('id', cloudId).single();
          const { data: equipmentData } = await supabase
            .from('equipment').select('*').eq('project_id', cloudId).order('sort_order');
          if (!pErr && projectData) {
            const resolvedAmi = (await getAmi(cloudId)) ??
              await fetchAndCacheAmiFromSupabase(cloudId).catch(() => []);
            if (activeProjectIdRef.current) return;
            dispatch({
              type: 'LOAD_PROJECT',
              payload: { projectId: cloudId, project: projectData, equipment: equipmentData || [], amiData: resolvedAmi },
            });
            await setCurrentProjectId(cloudId);
            if (uid) setLastProjectId(uid, cloudId);
            backfillAmiIfTableEmpty(cloudId, resolvedAmi).catch((e) => console.error('[amiBackfill] launch failed:', e));
            return;
          }
        }
      } catch { /* non-critical — fall through to naming prompt */ }
    }

    // Step 4: nothing eligible — ask the user to name a new project. An
    // account that has never seen the tour AND owns zero projects gets the
    // welcome/tour offer instead of the bare naming dialog.
    if (activeProjectIdRef.current) return;
    if (await isFirstRunEligible(uid)) {
      setShowWelcomeModal(true);
    } else {
      setShowProjectNameDialog(true);
    }
  }

  // Auto-offer check for the guided tour. The count is scoped to the user's
  // OWN projects (a firm member who can see colleagues' work is still new),
  // and any failure reads as ineligible — a transient error must fall back to
  // the ordinary naming dialog, never nag an existing user with the tour.
  async function isFirstRunEligible(uid: string | null): Promise<boolean> {
    if (!uid || userProfile?.onboarding_status != null) return false;
    try {
      const { count, error } = await supabase
        .from('projects')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', uid);
      if (error) return false;
      return isTourAutoOfferEligible(userProfile?.onboarding_status, count ?? null);
    } catch {
      return false;
    }
  }

  const handleProjectChange = (updates: Partial<Project>) => {
    dispatch({ type: 'UPDATE_PROJECT', payload: updates });
  };

  const handleAddEquipment = (seed?: Partial<Equipment>) => {
    const newEquipment: Equipment = {
      id: crypto.randomUUID(),
      project_id: projectId || '',
      equipment_name: '',
      equipment_type: '',
      // Nameplate, not blank: it is what the section counts, and it matches the
      // existing-load creator below. MCA is the exception a preparer chooses
      // when the label gives nothing else, and it is used as entered — TrueLoad
      // converts between the two in neither direction (nec-methodology.md).
      source: 'Nameplate',
      quantity: 1,
      amps: null,
      kw: null,
      voltage: 240,
      is_concurrent: false,
      concurrency_reason: null,
      sort_order: equipment.length,
      created_at: new Date().toISOString(),
      // Explicit so every in-memory row matches DB-loaded rows key-for-key;
      // the insert gateway (equipmentPayload.ts) backstops rows that predate
      // these fields.
      load_role: 'proposed',
      value_source: 'nameplate',
      on_laundry_circuit: false,
      // A quick-add chip seeds the row with the equipment the contractor
      // entered before; concurrency is then set by the schedule's own effect.
      ...seed,
    };
    dispatch({ type: 'ADD_EQUIPMENT', payload: newEquipment });
  };

  // Equipment taken out with this work: same row shape as a new load, role
  // 'removed'. Concurrency is set by the removed schedule's own effect, the
  // same season determination new loads get.
  const handleAddRemovedLoad = (seed?: Partial<Equipment>) => {
    handleAddEquipment({ load_role: 'removed', ...seed });
  };

  // An inventory row marked "Removed with this work" changes role in place, so
  // its rating comes with it. Concurrency is cleared for the removed
  // schedule to determine; moving back clears it again (inventory rows carry
  // none).
  const handleMarkRemoved = (id: string) => {
    handleUpdateEquipment(id, { load_role: 'removed', is_concurrent: false, concurrency_reason: null });
  };
  const handleReturnToInventory = (id: string) => {
    handleUpdateEquipment(id, { load_role: 'existing', is_concurrent: false, concurrency_reason: null });
  };

  // How removed equipment is treated on this project, in the preparer's words.
  // Mirrors measured22087.ts removedLoadCreditApplies and the traditional
  // engine's role filter.
  const removedTreatmentNote =
    resolvedEditionKey(project) === '2026'
      ? 'Measured demand (NEC 2026): each item that runs in the peak season is credited at 40% of its rating, subtracted after the 125%. Traditional calculation: removed equipment is simply not counted, because that calculation describes the house after the work.'
      : `Measured demand: listed on the report, not credited. The ${resolvedEditionKey(project)} edition gives the measured method no subtraction for removed equipment, and the meter already recorded it. Traditional calculation: removed equipment is simply not counted, because that calculation describes the house after the work.`;

  // Dwelling Profile rows: same table, load_role 'existing'. Seed values come
  // from the checklist; value_source starts 'default' there and flips to
  // 'nameplate' on edit.
  const handleAddExistingLoad = (seed: Partial<Equipment>) => {
    const row: Equipment = {
      id: crypto.randomUUID(),
      project_id: projectId || '',
      equipment_name: '',
      equipment_type: 'Other',
      source: 'Nameplate',
      quantity: 1,
      amps: null,
      kw: null,
      voltage: 240,
      is_concurrent: false,
      concurrency_reason: null,
      sort_order: equipment.length,
      created_at: new Date().toISOString(),
      load_role: 'existing',
      value_source: 'default',
      on_laundry_circuit: false,
      ...seed,
    };
    dispatch({ type: 'ADD_EQUIPMENT', payload: row });
  };

  // Turns one row of N identical units into N rows of one, in place. It has to
  // be a single dispatch: an update followed by an add would both read the same
  // captured `equipment`, and the second would discard the first.
  const handleSplitExistingLoad = (id: string) => {
    const index = equipment.findIndex((eq) => eq.id === id);
    if (index < 0) return;
    const source = equipment[index];
    const count = Math.floor(source.quantity);
    if (count < 2) return;
    const base = source.equipment_name.trim() || 'Fixed load';
    // The first row keeps its name so the dwelling checklist, which matches rows
    // by label, stays bound to it; the copies are numbered from 2.
    const copies: Equipment[] = Array.from({ length: count - 1 }, (_, i) => ({
      ...source,
      id: crypto.randomUUID(),
      equipment_name: `${base} ${i + 2}`,
      quantity: 1,
      created_at: new Date().toISOString(),
    }));
    const next = [
      ...equipment.slice(0, index),
      { ...source, quantity: 1 },
      ...copies,
      ...equipment.slice(index + 1),
    ].map((eq, i) => ({ ...eq, sort_order: i }));
    dispatch({ type: 'UPDATE_EQUIPMENT', payload: next });
  };

  const handleUpdateEquipment = (id: string, updates: Partial<Equipment>) => {
    dispatch({ type: 'PATCH_EQUIPMENT', payload: { id, updates } });
  };

  const handleDeleteEquipment = (id: string) => {
    dispatch({ type: 'REMOVE_EQUIPMENT', payload: id });
  };

  const handleUploadCSV = async (data: Array<{ timestamp: string; kw: number }>) => {
    // Demo guard. The demo seeds its series through dispatch and never comes
    // through here, but a real drop on the dropzone mid-demo would.
    if (isDemoProjectId(projectId)) return;
    const newAmiData: AMIDataPoint[] = data.map((point) => ({
      id: crypto.randomUUID(),
      project_id: projectId || '',
      timestamp: point.timestamp,
      kw: point.kw,
      created_at: new Date().toISOString(),
    }));
    dispatch({ type: 'UPDATE_AMI_DATA', payload: newAmiData });
    if (projectId) {
      try {
        await putAmi(projectId, newAmiData);
        // Persist to Supabase on every upload and replace, but only once the
        // project holds a real UUID. persistAmiIntervals self-guards on local_
        // and never throws, so it cannot block the upload.
        if (!projectId.startsWith('local_')) {
          const persisted = await persistAmiIntervals(projectId, newAmiData, 'upload');
          console.log(
            `[amiUpload] AMI persist for ${projectId}: state=${persisted.state} ` +
            `sent=${persisted.sent} inserted=${persisted.inserted}` +
            (persisted.error ? ` error=${persisted.error}` : '')
          );
        }
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        dispatch({ type: 'UPDATE_AMI_DATA', payload: [] });
        alert(`AMI data could not be durably saved (IndexedDB error: ${msg}). Upload cancelled.`);
      }
    }
  };

  /**
   * Delete the project's measured series — everywhere it is stored.
   *
   * This used to clear React state and the IndexedDB cache and stop there. The
   * ami_data_points rows and the AMI-derived columns on the projects row were
   * never touched, so the next page load found an empty cache, fell back to
   * Supabase (loadInitialProject / handleLoadProject both do), and handed the
   * "deleted" data straight back. Deleting has to reach all three stores.
   *
   * The server delete goes FIRST and un-awaited on purpose. deleteAmiIntervals
   * registers itself in the persistence layer's in-flight chain synchronously,
   * which is what puts it ahead of the replacement upload on the Replace path —
   * that upload's save then chains behind this delete instead of racing it.
   * Awaiting the returned promise is not needed for that ordering, and the
   * function never throws: a failure publishes to the status banner instead.
   *
   * Every AMI-derived project field is cleared alongside the readings, and the
   * server clears the same set inside the RPC's transaction (see migration
   * 20260903203222). Left behind, max_recorded_demand_kw alone would keep the
   * results populated and pin the project to the measured path.
   *
   * report_method is cleared only when it reads 'measured', matching the RPC.
   * It is the stored report basis, and nothing is left on the measured path to
   * be a basis for; neither place that stamps it will correct a value that is
   * already set, so leaving it would strand the project on an empty path.
   */
  // A homeowner upload landed on the open project. The readings are on the
  // server; drop this device's cache and open the project again, which is the
  // same path a first open on a new device takes.
  const handleReloadProjectFromCloud = async (): Promise<void> => {
    if (!projectId || projectId.startsWith('local_') || isDemoProjectId(projectId)) return;
    try {
      await deleteAmi(projectId);
    } catch (e) {
      console.error('[reloadProject] failed to clear the IDB cache:', e);
    }
    // The contractor is on Existing Load watching the readings arrive. Stay there.
    await handleLoadProject(projectId, { keepTab: true });
  };

  const handleDeleteAMIData = async (): Promise<void> => {
    // Demo guard, the same one every other write path consults: the sample
    // project exists only in memory, so there is nothing of it to delete from
    // IndexedDB or Supabase — and 'demo_guided_tour' is not a UUID, so handing
    // it to the RPC would raise a type error and put a red banner over the tour.
    const storedId = projectId && !isDemoProjectId(projectId) ? projectId : null;

    if (storedId) {
      void deleteAmiIntervals(storedId, 'delete-ami');
    }

    dispatch({ type: 'UPDATE_AMI_DATA', payload: [] });
    dispatch({
      type: 'UPDATE_PROJECT',
      payload: {
        ami_measurement_period: '',
        ami_interval_minutes: null,
        ami_data_quality_notes: '',
        ami_site_notes: '',
        max_recorded_demand_kw: null,
        peak_timestamp: null,
        gross_peak_kw: null,
        ami_generation_column_detected: false,
        ami_merge_conflicts_resolved: null,
        ami_merge_duplicates_removed: null,
        ami_merge_strategy: null,
        ami_exclusions: null,
        ami_estimated_runs: null,
        ...(project.report_method === 'measured' ? { report_method: null } : {}),
      },
    });

    // Awaited, and the caller awaits this handler in turn: on the Replace path
    // the replacement upload writes the new series to the same IDB key the
    // moment it finishes parsing, and a delete still in flight would land on
    // top of it.
    if (storedId) {
      try {
        await deleteAmi(storedId);
      } catch (e) {
        console.error('[deleteAmi] failed to clear the IDB cache:', e);
      }
    }
  };

  const handleSave = async (): Promise<string | null> => {
    // Demo guard. Reached if the user presses Save while the demo is playing;
    // the sample project has nowhere legitimate to go.
    if (isDemoProjectId(projectId)) {
      setSaveMessage('Demo mode — the sample project is not saved.');
      setTimeout(() => setSaveMessage(''), 3000);
      return null;
    }
    if (isFirmViewOnly) {
      setSaveMessage("You can view and generate reports on this project, but can't save changes to it.");
      setTimeout(() => setSaveMessage(''), 4000);
      return null;
    }
    setLoading(true);
    setSaveMessage('');

    try {
      let resultId: string | null = null;
      const currentProjectId = projectId || generateLocalProjectId();

      await saveProjectLocally(currentProjectId, project, equipment, amiData, user?.id ?? undefined);

      if (!projectId) {
        dispatch({ type: 'SET_PROJECT_ID', payload: currentProjectId });
      }

      if (!user) {
        setSaveMessage('Project saved locally');
        setTimeout(() => setSaveMessage(''), 3000);
        setLoading(false);
        return null;
      }

      const now = new Date().toISOString();

      if (projectId && !projectId.startsWith('local_')) {
        const { error: projectError } = await supabase
          .from('projects')
          .update({
            ...toSupabasePayload(project as Project),
            updated_at: now,
            last_opened_at: now,
          })
          .eq('id', projectId);

        if (projectError) throw projectError;

        await saveProjectLocally(projectId, project, equipment, amiData, user?.id ?? undefined);

        await supabase.from('equipment').delete().eq('project_id', projectId);

        if (equipment.length > 0) {
          const { error: equipmentError } = await supabase
            .from('equipment')
            .insert(toEquipmentInsertRows(equipment, projectId));

          if (equipmentError) throw equipmentError;
        }

        resultId = projectId;
      } else {
        if (isPromotingRef.current) return null;
        isPromotingRef.current = true;
        try {
          // INVARIANT: Project creation must be idempotent — this branch runs
          // exactly once per project lifetime (local_ → UUID promotion).
          // Do NOT call insert here for any other reason.
          const { data: newProject, error: projectError } = await supabase
            .from('projects')
            .insert({
              ...toSupabasePayload(project as Project),
              user_id: user.id,
              last_opened_at: now,
            })
            .select()
            .single();

          if (projectError) throw projectError;

          // Promote: assign the stable Supabase UUID and erase the temporary
          // local_ entry so it never appears as a duplicate in the project list.
          dispatch({ type: 'SET_PROJECT_ID', payload: newProject.id });
          await deleteLocalProject(currentProjectId); // remove local_ entry from localStorage
          if (user?.id) setLastProjectId(user.id, newProject.id);

          // Migrate IDB AMI record from old local_ key to the new Supabase UUID.
          if (currentProjectId.startsWith('local_')) {
            const oldAmi = await getAmi(currentProjectId);
            if (oldAmi) {
              await putAmi(newProject.id, oldAmi);
              await deleteAmi(currentProjectId); // remove old IDB entry
            }
          }

          if (equipment.length > 0) {
            const { error: equipmentError } = await supabase
              .from('equipment')
              .insert(toEquipmentInsertRows(equipment, newProject.id));

            if (equipmentError) throw equipmentError;
          }

          // Persist interval rows to the new UUID via the single gateway, same as
          // the autosave promote path. Self-guards on empty/local; never throws.
          const promotedAmi = await getAmi(newProject.id);
          const amiPersisted = await persistAmiIntervals(newProject.id, promotedAmi ?? [], 'save-promote');
          console.log(
            `[save-promote] AMI persist for ${newProject.id}: state=${amiPersisted.state} ` +
            `sent=${amiPersisted.sent} inserted=${amiPersisted.inserted}` +
            (amiPersisted.error ? ` error=${amiPersisted.error}` : '')
          );

          resultId = newProject.id;
        } finally {
          isPromotingRef.current = false;
        }
      }

      setSaveMessage('Project saved to cloud');
      setTimeout(() => setSaveMessage(''), 3000);
      return resultId;
    } catch (error) {
      console.error('Save error:', error);
      setSaveMessage('Project saved locally (cloud sync unavailable)');
      setTimeout(() => setSaveMessage(''), 4000);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const handleNewProject = () => {
    setShowProjectNameDialog(true);
  };

  // ---- Guided demo ------------------------------------------------------
  // Parks the user's real project, loads the sample one, and hands the screen
  // to DemoOverlay. Nothing here writes anywhere: see the `inDemo` guards on
  // every save path above.
  const startDemo = useCallback(async () => {
    preDemoRef.current = { projectId, project, equipment, amiData };

    // A real utility id makes the Service Info dropdown show a real selection.
    // If the lookup fails the demo still runs; the field just stays unset.
    let utilityId: string | null = null;
    // Burlington Electric, not Green Mountain Power: the sample job is at 45 Elm
    // Street, Burlington, and BED is who serves that ZIP. A contractor who works
    // there would spot the wrong utility immediately.
    let utilityName = 'Burlington Electric Department';
    try {
      const { data } = await supabase
        .from('utilities')
        .select('id, name')
        .ilike('name', '%burlington electric%')
        .limit(1);
      if (data && data.length > 0) {
        utilityId = data[0].id;
        utilityName = data[0].name;
      }
    } catch {
      // Non-critical — the demo is about the workflow, not this one field.
    }

    dispatch({
      type: 'LOAD_PROJECT',
      payload: {
        projectId: DEMO_PROJECT_ID,
        project: buildDemoProject(),
        equipment: [],
        amiData: [],
      },
    });
    activeProjectIdRef.current = DEMO_PROJECT_ID;
    setActiveTab('project');
    setDemoSeed({
      preparer: DEMO_PREPARER,
      utilityId,
      utilityName,
    });
  }, [projectId, project, equipment, amiData]);

  const endDemo = useCallback(
    (completed: boolean) => {
      setDemoSeed(null);
      demoAmiRef.current = null;
      demoSeasonRef.current = null;
      void setOnboardingStatus(completed ? 'completed' : 'declined');

      const snapshot = preDemoRef.current;
      preDemoRef.current = null;

      if (snapshot?.projectId) {
        // Hand the user's own project back exactly as they left it.
        dispatch({ type: 'LOAD_PROJECT', payload: { ...snapshot, projectId: snapshot.projectId } });
        activeProjectIdRef.current = snapshot.projectId;
        setActiveTab('project');
        return;
      }

      dispatch({ type: 'CLEAR_PROJECT' });
      activeProjectIdRef.current = null;
      setActiveTab('project');
      // Nothing to go back to — which is the whole point of the demo. Send
      // them straight into naming their real first job.
      setShowProjectNameDialog(true);
    },
    [setOnboardingStatus],
  );

  // Built once per demo. The job is rebuilt on every navigation, and
  // regenerating 35,040 readings each time would make Back visibly stutter.
  const demoAmiRef = useRef<AMIDataPoint[] | null>(null);
  // Peak season for the demo's equipment rows.
  //
  // EquipmentSchedule normally auto-fills concurrency through its own effect,
  // but the demo writes to the same rows within the same beat: its next write
  // reads a ref that has not yet caught that effect's update, and overwrites
  // it. The heat pump water heater came out non-concurrent that way even though
  // the lookup says a water heater runs year-round. Applying the call on the
  // demo's own writes makes it deterministic instead of a race.
  const demoSeasonRef = useRef<string | null>(null);
  const demoAmiSeries = useCallback(() => {
    if (!demoAmiRef.current) demoAmiRef.current = buildDemoAmiSeries({ endDate: new Date() });
    return demoAmiRef.current;
  }, []);

  /** Loads the sample year of interval data, as a real upload would. */
  const handleDemoSeedAmi = useCallback(() => {
    const series = demoAmiSeries();
    const fields = demoAmiFields(series, demoSeed?.utilityName);
    demoSeasonRef.current = determineSeason(fields.peak_timestamp ?? null);
    dispatch({ type: 'UPDATE_AMI_DATA', payload: series });
    dispatch({ type: 'UPDATE_PROJECT', payload: fields });
    // Re-apply to rows already on the schedule: seeding the data is what makes
    // a concurrency call possible at all.
    const { proposed, existing } = splitDemoRows(equipmentRef.current);
    dispatch({
      type: 'UPDATE_EQUIPMENT',
      payload: [
        ...materializeDemoEquipment(proposed, demoSeasonRef.current, new Date().toISOString()),
        ...existing,
      ],
    });
  }, [demoSeed?.utilityName, demoAmiSeries]);

  /**
   * Replaces the whole sample job with the state a beat should start from.
   *
   * The demo never undoes a step; it replays the earlier ones and swaps the
   * result in wholesale. That is what makes going Back start a scene from blank
   * fields, and what stops revisiting the equipment beat leaving a second copy
   * of the same heat pump behind.
   */
  const handleDemoApplyState = useCallback(
    (state: DemoState) => {
      const now = new Date().toISOString();
      const amiData = state.ami ? demoAmiSeries() : [];
      // Season drives the concurrency call, and without AMI data there is none.
      const season = state.ami ? determineSeason(demoAmiFields(amiData).peak_timestamp ?? null) : null;
      demoSeasonRef.current = season;
      const rows = [
        ...materializeDemoEquipment(state.equipment, season, now),
        ...materializeDemoInventory(state.existing, now),
      ];
      dispatch({
        type: 'LOAD_PROJECT',
        payload: {
          projectId: DEMO_PROJECT_ID,
          project: {
            ...buildDemoProject(),
            ...(state.ami ? demoAmiFields(amiData, demoSeed?.utilityName) : {}),
            ...state.project,
          },
          equipment: rows,
          amiData,
        },
      });
      activeProjectIdRef.current = DEMO_PROJECT_ID;
      // The open sub-path of the Existing Load tab is part of the rebuilt
      // state too: stepping back to the fork shows the chooser again, with
      // nothing picked.
      setExistingLoadMode(state.mode);
    },
    [demoSeed?.utilityName, demoAmiSeries],
  );

  /**
   * Appends a blank row for the demo.
   *
   * Cannot reuse handleAddEquipment: that one closes over `equipment` from the
   * render it was created in, and the demo's context is built before the
   * rebuild dispatch has committed — so it would append to a stale array and
   * silently lose the row it just added.
   */
  const handleDemoAddEquipment = useCallback(() => {
    const { proposed, existing } = splitDemoRows(equipmentRef.current);
    dispatch({
      type: 'UPDATE_EQUIPMENT',
      payload: [
        ...materializeDemoEquipment([...proposed, {}], demoSeasonRef.current, new Date().toISOString()),
        ...existing,
      ],
    });
  }, []);

  /**
   * Fills the row the demo is working on, creating it if there isn't one.
   *
   * The upsert is what makes a beat's `settle` idempotent: whether the visible
   * "add row" step happened or the user skipped straight past it, the sample
   * job ends up with one complete equipment row. Reads through a ref because
   * the demo adds and fills within a single beat.
   */
  const handleDemoUpdateEquipment = useCallback((updates: Partial<Equipment>) => {
    const { proposed, existing } = splitDemoRows(equipmentRef.current);
    const next =
      proposed.length === 0
        ? [{ ...updates }]
        : proposed.map((eq, i) => (i === proposed.length - 1 ? { ...eq, ...updates } : eq));
    dispatch({
      type: 'UPDATE_EQUIPMENT',
      payload: [
        ...materializeDemoEquipment(next, demoSeasonRef.current, new Date().toISOString()),
        ...existing,
      ],
    });
  }, []);

  /**
   * Adds a dwelling-inventory row for the demo, or updates the one already
   * carrying that name — the same upsert the Dwelling Profile checklist does
   * when a quantity is set, which is what lets a replayed checklist beat
   * settle without listing the range twice.
   */
  const handleDemoAddExisting = useCallback((seed: Partial<Equipment>) => {
    const { proposed, existing } = splitDemoRows(equipmentRef.current);
    const at = existing.findIndex((eq) => eq.equipment_name === seed.equipment_name);
    const next: Partial<Equipment>[] =
      at >= 0 ? existing.map((eq, i) => (i === at ? { ...eq, ...seed } : eq)) : [...existing, seed];
    dispatch({
      type: 'UPDATE_EQUIPMENT',
      payload: [...proposed, ...materializeDemoInventory(next, new Date().toISOString())],
    });
  }, []);

  const handleCreateProject = async (projectName: string) => {
    if (projectId) {
      await saveProjectLocally(projectId, project, equipment, amiData, user?.id ?? undefined);
    }

    const newProjectId = generateLocalProjectId();
    const newProject: Partial<Project> = {
      project_name: projectName,
      status: 'in-progress',
      // See initialProjectState: entered, never assumed.
      service_rating_amps: 0,
      service_voltage_numeric: 240,
      service_voltage_text: '120/240V single-phase',
      service_phase: 'single',
      service_type: 'unknown',
      installation_status: 'existing',
      methodology_version: 'NEC 220.87 v1.0',
      // Preparer Information comes from the Company Profile; report date
      // defaults to the creation date, which for a new project is today.
      ...preparerDefaults(companyProfile),
    };

    dispatch({ type: 'SET_LOADING', payload: true });
    dispatch({
      type: 'LOAD_PROJECT',
      payload: {
        projectId: newProjectId,
        project: newProject,
        equipment: [],
        amiData: [],
      },
    });
    activeProjectIdRef.current = newProjectId;

    await setCurrentProjectId(newProjectId);
    if (user?.id) setLastProjectId(user.id, newProjectId);
    dispatch({ type: 'SET_LOADING', payload: false });

    setActiveTab('project');
    setShowProjectNameDialog(false);
  };

  // Step 1: run validation, show modal — does NOT change project state
  const handleInitiateComplete = async () => {
    if (!projectId) return;
    // Full equipment list on purpose: the validator splits proposed rows from
    // dwelling-inventory rows itself, per the project's report basis.
    const result = await validateProjectForCompletion(projectId, project, equipment, amiInEffect);
    setValidationResult(result);
    if (result.summary.blockerCount > 0) {
      setShowRequiredHighlights(true);
    }
    setShowValidationModal(true);
  };

  // Step 2: called by modal after user confirms (no blockers, warnings acknowledged)
  const handleMarkComplete = async (warningAcknowledged: boolean) => {
    if (!projectId) return;
    setShowValidationModal(false);

    try {
      // Auto-set report_date if not already set
      const today = new Date().toISOString().split('T')[0];
      const projectWithDate = project.report_date
        ? project
        : { ...project, report_date: today };

      // Freeze the code edition. A project still following its state default
      // gets that edition stored now, so the report it was completed under
      // cannot drift if the state's adopted edition changes later. An
      // explicit choice is already stored and stands.
      // The calculation rules freeze the same way (calc/rulesVersion.ts): the
      // revision this project resolves to now is the one its report keeps.
      const updatedProject = {
        ...projectWithDate,
        code_edition: resolvedEditionKey(projectWithDate),
        calc_rules_version: calcRulesFor(projectWithDate),
        status: 'completed' as const,
      };

      // CRITICAL FIX: Update full project state in React, not just status field
      // This ensures React state matches localStorage and prevents race conditions
      dispatch({ type: 'UPDATE_PROJECT', payload: updatedProject });

      await saveProjectLocally(projectId, updatedProject, equipment, amiData, user?.id ?? undefined);

      // Persist validation snapshot (counts + timestamp) for traceability
      if (validationResult) {
        saveValidationSnapshot(projectId, {
          completedAt: new Date().toISOString(),
          blockerCount: validationResult.summary.blockerCount,
          warningCount: validationResult.summary.warningCount,
          advisoryCount: validationResult.summary.advisoryCount,
          warningAcknowledged,
        });
      }

      if (user && !projectId.startsWith('local_')) {
        const now = new Date().toISOString();
        // pass_fail records the adequacy of the report basis. The traditional
        // runner takes the full list and splits roles itself.
        const calcResult =
          updatedProject.report_method === 'traditional' && canRunTraditional(updatedProject)
            ? runTraditionalCalc(updatedProject, equipment)
            : calculateNEC22087(updatedProject as Project, enteredProposedLoads(equipment), readingsInEffect(amiData, updatedProject), enteredRemovedLoads(equipment));
        const completionValidationPayload = validationResult ? {
          blockerCount: validationResult.summary.blockerCount,
          warningCount: validationResult.summary.warningCount,
          advisoryCount: validationResult.summary.advisoryCount,
          warningAcknowledged,
          validatedAt: now,
          blockerIds: validationResult.blockers.map(b => b.id),
          warningIds: validationResult.warnings.map(w => w.id),
        } : null;

        const { error: updateError } = await supabase
          .from('projects')
          .update({
            ...toSupabasePayload(updatedProject as Project),
            updated_at: now,
            last_opened_at: now,
            status: 'completed',
            completed_at: now,
            pass_fail: calcResult.adequacyResult,
            completion_validation: completionValidationPayload,
          })
          .eq('id', projectId);

        if (updateError) {
          console.error('Supabase update error:', updateError);
          alert(`Failed to mark project as complete: ${updateError.message}`);
          throw updateError;
        }

        await supabase.from('equipment').delete().eq('project_id', projectId);
        if (equipment.length > 0) {
          await supabase
            .from('equipment')
            .insert(toEquipmentInsertRows(equipment, projectId));
        }

        // Backstop only — rows normally persist on upload/promote. Idempotent
        // via the single gateway; no-ops (and never wipes rows) on an empty series.
        await persistAmiIntervals(projectId, amiData, 'mark-complete');
      }

      setSaveMessage('Project marked as complete');
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (error) {
      console.error('Error marking project complete:', error);
      setSaveMessage('Failed to mark complete');
      setTimeout(() => setSaveMessage(''), 3000);
    }
  };

  const handleUnlockProject = async () => {
    if (!projectId) return;

    try {
      const updatedProject = { ...project, status: 'in-progress' as ProjectStatus };

      // CRITICAL FIX: Update full project state in React, not just status field
      // This ensures React state matches localStorage and prevents race conditions
      dispatch({ type: 'UPDATE_PROJECT', payload: updatedProject });

      try {
        await saveProjectLocally(projectId, updatedProject, equipment, amiData, user?.id ?? undefined);
      } catch (localErr) {
        console.error('saveProjectLocally failed:', localErr);
        throw localErr;
      }

      console.log('user at unlock time:', user);

      if (user && !projectId.startsWith('local_')) {
        const now = new Date().toISOString();

        const { error: unlockError } = await supabase
          .from('projects')
          .update({
            status: 'in-progress',
            updated_at: now
          })
          .eq('id', projectId);

        if (unlockError) {
          console.error('Supabase unlock error:', unlockError);
          throw unlockError;
        }
      }

      setSaveMessage('Project unlocked for editing');
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (error: unknown) {
      const e = error as { name?: string; message?: string; stack?: string };
      console.error('handleUnlockProject catch:', { name: e?.name, message: e?.message, stack: e?.stack });
      setSaveMessage(`Unlock error: ${e?.name} - ${e?.message}`);
      setTimeout(() => setSaveMessage(''), 3000);
    } finally {
      setShowUnlockDialog(false);
    }
  };

  // Fetch all AMI rows for a Supabase project. Caches the result in IDB so
  // subsequent loads on this device skip the network round-trip. Paging lives
  // in amiFetch.ts, shared with the admin read-only viewer — which has no IDB
  // cache and so pays the full cost on every open.
  async function fetchAndCacheAmiFromSupabase(projectId: string): Promise<AMIDataPoint[]> {
    const all = await fetchAllAmiPoints(supabase, projectId);
    if (all.length > 0) {
      await putAmi(projectId, all);
    }
    return all;
  }

  // One-time backfill for projects created before AMI writes were routed through
  // persistAmiIntervals: if a real-UUID project has in-memory/IDB AMI but its
  // ami_data_points table is empty, persist the series once on open. The count
  // read happens immediately before the persist call, and persistAmiIntervals'
  // in-flight guard serializes against any concurrent upload/promote — so if an
  // upload is already writing this project, backfill awaits that write rather than
  // starting a second one. Callers chain (not bare-void) so failures are logged.
  async function backfillAmiIfTableEmpty(pid: string, amiData: AMIDataPoint[]): Promise<void> {
    if (!pid || pid.startsWith('local_') || isDemoProjectId(pid) || !amiData || amiData.length === 0) return;
    try {
      const { count, error } = await supabase
        .from('ami_data_points')
        .select('*', { count: 'exact', head: true })
        .eq('project_id', pid);
      if (error) return;
      if ((count ?? 0) === 0) {
        const persisted = await persistAmiIntervals(pid, amiData, 'backfill');
        console.log(
          `[amiBackfill] ${pid}: table empty on load, state=${persisted.state} ` +
          `sent=${persisted.sent} inserted=${persisted.inserted}` +
          (persisted.error ? ` error=${persisted.error}` : '')
        );
      }
    } catch (err) {
      console.error(`[amiBackfill] ${pid} failed:`, err);
    }
  }

  const handleLoadProject = async (loadProjectId: string, options?: { keepTab?: boolean }) => {
    // Reset firm-project tracking — assume own project until told otherwise
    setFirmProjectOwnerName(null);
    setFirmProjectEditPermission('own_only');
    try {
      setLoading(true);
      dispatch({ type: 'SET_LOADING', payload: true });

      if (projectId) {
        await saveProjectLocally(projectId, project, equipment, amiData, user?.id ?? undefined);
        if (amiData.length > 0) {
          await putAmi(projectId, amiData);
        }
      }

      if (loadProjectId.startsWith('local_')) {
        const localProject = await loadProjectLocally(loadProjectId);
        if (localProject) {
          const idbAmi = await getAmi(loadProjectId);
          dispatch({
            type: 'LOAD_PROJECT',
            payload: {
              projectId: loadProjectId,
              project: localProject.project,
              equipment: localProject.equipment,
              amiData: idbAmi ?? localProject.amiData,
            },
          });
          activeProjectIdRef.current = loadProjectId;

          await setCurrentProjectId(loadProjectId);
          if (user?.id) setLastProjectId(user.id, loadProjectId);
          dispatch({ type: 'SET_LOADING', payload: false });

          setActiveTab('project');
          setShowProjectManager(false);
        } else {
          dispatch({ type: 'SET_LOADING', payload: false });
          alert('Failed to load local project');
        }
      } else {
        const { data: projectData, error: projectError } = await supabase
          .from('projects')
          .select('*')
          .eq('id', loadProjectId)
          .single();

        if (projectError) throw projectError;

        const { data: equipmentData, error: equipmentError } = await supabase
          .from('equipment')
          .select('*')
          .eq('project_id', loadProjectId)
          .order('sort_order');

        if (equipmentError) throw equipmentError;

        // IDB is the primary AMI source on this device. On an IDB miss
        // (e.g. first load on mobile or a new browser), fall back to
        // Supabase and cache the result so future loads are local.
        const resolvedAmiData =
          (await getAmi(loadProjectId)) ??
          (await fetchAndCacheAmiFromSupabase(loadProjectId));

        dispatch({
          type: 'LOAD_PROJECT',
          payload: {
            projectId: loadProjectId,
            project: projectData,
            equipment: equipmentData || [],
            amiData: resolvedAmiData,
          },
        });
        activeProjectIdRef.current = loadProjectId;
        backfillAmiIfTableEmpty(loadProjectId, resolvedAmiData).catch((e) => console.error('[amiBackfill] launch failed:', e));

        // Readings with no summary. A homeowner upload writes both in one
        // transaction, but a contractor's autosave in flight at that moment
        // carries the stale null peak and can land on top. The readings are the
        // truth; recompute the summary from them and let the save carry it.
        if (resolvedAmiData.length > 0 && projectData.max_recorded_demand_kw == null) {
          const summary = summarizeAmiSeries(readingsInEffect(resolvedAmiData, projectData));
          dispatch({
            type: 'UPDATE_PROJECT',
            payload: {
              max_recorded_demand_kw: summary.peakKw,
              peak_timestamp: summary.peakTimestamp,
              ami_interval_minutes: summary.intervalMinutes,
              ami_measurement_period: summary.measurementPeriod,
            },
          });
        }

        await setCurrentProjectId(loadProjectId);
        if (user?.id) setLastProjectId(user.id, loadProjectId);
        dispatch({ type: 'SET_LOADING', payload: false });

        await supabase
          .from('projects')
          .update({ last_opened_at: new Date().toISOString() })
          .eq('id', loadProjectId);

        if (!options?.keepTab) setActiveTab('project');
        setShowProjectManager(false);
      }
    } catch (error) {
      console.error('Error loading project:', error);
      dispatch({ type: 'SET_LOADING', payload: false });
      alert('Failed to load project');
    } finally {
      setLoading(false);
    }
  };


  const handleLoadFirmProject = async (
    projectId: string,
    ownerName: string | null,
    editPermission: string
  ) => {
    setFirmProjectOwnerName(ownerName);
    setFirmProjectEditPermission(editPermission);
    await handleLoadProject(projectId);
  };

  const tabs = [
    { id: 'project' as Tab, label: 'Project Info', icon: FileText, testId: 'tab-project-info' },
    { id: 'service' as Tab, label: 'Service Info', icon: Zap, testId: 'tab-service-info' },
    { id: 'ami' as Tab, label: 'Existing Load', icon: Database, testId: 'tab-ami' },
    { id: 'equipment' as Tab, label: 'New Loads', icon: ClipboardList, testId: 'tab-equipment' },
    { id: 'results' as Tab, label: 'Results', icon: BarChart3, testId: 'tab-results' },
    { id: 'help' as Tab, label: 'Help', icon: HelpCircle, testId: 'tab-help' },
  ];

  // The strip and dots usher a project toward completion, so they are for
  // projects still being worked: not a locked one, not someone else's.
  const showProgress = !!projectId && !isReadOnly && !isFirmViewOnly;

  // Only service_rating_amps is needed to enable the Results tab.
  // AMI data presence and paywall gating are handled inside ResultsView.
  const canViewResults = !!project.service_rating_amps;

  // Terminal bootstrap error — the app could not reach the server to load the
  // account. This takes precedence over the Loading spinner so "Loading..." can
  // never be a permanent state: bootstrap always exits to loaded, login, or here.
  if (bootstrapError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4" data-testid="bootstrap-error">
        <div className="max-w-md w-full bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
          <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-6 h-6 text-amber-600" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Couldn't reach the server</h2>
          <p className="text-gray-600 text-sm mb-6">
            TrueLoad couldn't connect to load your account. This is usually a temporary
            connection problem. Check your internet connection and try again.
          </p>
          <button
            onClick={() => retryBootstrap()}
            data-testid="btn-bootstrap-retry"
            className="px-5 py-2.5 bg-blue-600 text-white rounded-md text-sm font-semibold hover:bg-blue-700"
          >
            Retry
          </button>
          {bootstrapErrorDetail && (
            <p className="mt-4 text-xs text-gray-400">{bootstrapErrorDetail}</p>
          )}
        </div>
      </div>
    );
  }

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (isPasswordReset) {
    return <ResetPasswordForm />;
  }

  if (!user) {
    if (authView === 'signup') {
      return (
        <SignupForm
          onSwitchToLogin={() => setAuthView('login')}
          onForgotPassword={() => setAuthView('forgot-password')}
          firmInvite={firmInvite}
        />
      );
    }
    if (authView === 'forgot-password') {
      return <ForgotPasswordForm onBack={() => setAuthView('login')} />;
    }
    return (
      <LoginForm
        onSwitchToSignup={() => setAuthView('signup')}
        onForgotPassword={() => setAuthView('forgot-password')}
        firmInvite={firmInvite}
        notice={authNotice}
      />
    );
  }

  // MFA gate — a signed-in session that still owes a second factor (or a
  // required enrollment) sees nothing but the MFA screen. The database
  // enforces the same boundary via restrictive policies; this is the UX for
  // it. Profile loading is deferred until the gate clears (AuthContext).
  if (mfaGate === 'challenge') {
    return <MfaChallengeScreen />;
  }
  if (mfaGate === 'enroll') {
    return <MfaEnrollmentScreen />;
  }

  // Disabled account gate — block all app access
  if (isDisabled) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
          <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="w-6 h-6 text-red-600" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Account Disabled</h2>
          <p className="text-gray-600 text-sm mb-6">
            Your account has been disabled. Please contact your administrator for assistance.
          </p>
          <button
            onClick={() => signOut()}
            className="px-4 py-2 bg-gray-800 text-white rounded-md text-sm hover:bg-gray-900"
          >
            Sign Out
          </button>
        </div>
      </div>
    );
  }

  // Admin dashboard — full-page takeover
  if (showAdminDashboard) {
    return <AdminDashboard onClose={() => setShowAdminDashboard(false)} />;
  }

  // Firm admin dashboard — full-page takeover
  if (showFirmDashboard) {
    return <FirmAdminDashboard onClose={() => setShowFirmDashboard(false)} />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header, tabs, and the progress strip scroll as one locked block. A
          single sticky ancestor is what keeps them together — the tab bar used
          to sticky on its own at a hardcoded offset shorter than the header,
          so it slid underneath and vanished. The header is compact from the
          first paint (context/ux.md item 9): the tool's own chrome should not
          take a quarter of the screen before the job does. */}
      <div className="sticky top-0 z-40 shadow-sm" data-sticky-header>
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-2">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-0">
            <div className="flex items-center">
              <img
                src="/trueload_logo.svg"
                alt="TrueLoad"
                className="w-auto flex-shrink-0 h-10 sm:h-12"
              />
              {firmLogoUrl && (
                <>
                  {/* Firm logo: the member's own mark beside TrueLoad's, sized
                      to read as a co-signature rather than compete with it. */}
                  <div aria-hidden="true" className="border-l border-gray-300 mx-3 sm:mx-4 h-6" />
                  <img
                    src={firmLogoUrl}
                    alt={firmName ? `${firmName} logo` : 'Firm logo'}
                    title={firmName ?? undefined}
                    data-testid="header-firm-logo"
                    onError={() => setFirmLogoUrl(null)}
                    className="w-auto max-w-[7rem] sm:max-w-[12rem] object-contain flex-shrink-0 h-7 sm:h-8"
                  />
                </>
              )}
            </div>
            <div className="flex items-center gap-2 w-full sm:w-auto" data-testid="header-actions">
              {projectId && (
                <p className="text-xs sm:text-sm font-medium text-gray-900 truncate mr-2 flex-1 sm:flex-none min-w-0">
                  {project.project_name}
                </p>
              )}
              {/* Projects is the one accented action; New lives inside it. */}
              <button
                onClick={() => setShowProjectManager(true)}
                data-testid="btn-project-manager"
                className="flex items-center space-x-1 sm:space-x-2 px-2 sm:px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors touch-manipulation"
              >
                <FolderOpen className="w-4 h-4 sm:w-5 sm:h-5" />
                <span className="text-sm sm:text-base">Projects</span>
              </button>
              {/* Save is a status word that still saves on click: the app
                  autosaves, so the word says when, and the click is "save
                  now". The harness drives this control by its id. */}
              <button
                onClick={handleSave}
                disabled={loading}
                data-testid="btn-save"
                title="Save now"
                className="flex items-center gap-1.5 px-2 sm:px-3 py-2 rounded-md text-xs sm:text-sm text-gray-600 hover:bg-gray-100 hover:text-gray-900 disabled:text-gray-400 transition-colors touch-manipulation whitespace-nowrap"
              >
                <Save className="w-4 h-4" />
                <span>
                  {loading
                    ? 'Saving…'
                    : lastSaveTime
                      ? `Saved ${lastSaveTime.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`
                      : 'Save'}
                </span>
              </button>
              <button
                onClick={() => {
                  setAccountSettingsTab('profile');
                  setShowAccountSettings(true);
                }}
                data-testid="btn-account-settings"
                className="flex items-center px-2 sm:px-3 py-2 rounded-md bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors touch-manipulation"
                title="Account Settings"
              >
                <Settings className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
              {userProfile?.firm_id && !userProfile.firm_admin && (
                <span className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-md text-xs font-medium text-blue-800">
                  <Building2 className="w-3.5 h-3.5" />
                  {firmName ?? 'Firm Member'}
                </span>
              )}
              {isAdmin && (
                <button
                  onClick={() => setShowAdminDashboard(true)}
                  className="flex items-center px-2 sm:px-3 py-2 rounded-md bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors touch-manipulation"
                  title="Admin Dashboard"
                >
                  <ShieldCheck className="w-4 h-4 sm:w-5 sm:h-5" />
                </button>
              )}
              {userProfile?.firm_admin && (
                <button
                  onClick={() => setShowFirmDashboard(true)}
                  className="flex items-center px-2 sm:px-3 py-2 rounded-md bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors touch-manipulation"
                  title="Firm Dashboard"
                  data-testid="btn-firm-dashboard"
                >
                  <Building2 className="w-4 h-4 sm:w-5 sm:h-5" />
                </button>
              )}
              {saveMessage && (
                <span data-testid="save-status" className="hidden md:inline text-sm text-green-600 font-medium">{saveMessage}</span>
              )}
            </div>
          </div>
        </div>
      </header>

      <nav className="bg-white border-b border-gray-200" data-testid="tab-bar">
        <div className="max-w-7xl mx-auto px-2 sm:px-6 lg:px-8">
          <div className="flex space-x-1 sm:space-x-4 lg:space-x-8 overflow-x-auto scrollbar-hide">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              const isDisabled = tab.id === 'results' && !canViewResults;
              const section = progress.sections.find((s) => s.tab === tab.id);

              return (
                <button
                  key={tab.id}
                  onClick={() => !isDisabled && setActiveTab(tab.id)}
                  disabled={isDisabled}
                  data-testid={tab.testId}
                  className={`flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-3 sm:py-4 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap transition-colors touch-manipulation ${
                    isActive
                      ? 'border-blue-600 text-blue-600'
                      : isDisabled
                      ? 'border-transparent text-gray-400 cursor-not-allowed'
                      : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
                  }`}
                >
                  <Icon size={24} className="sm:w-[20px] sm:h-[20px]" />
                  <span className="hidden xs:inline">{tab.label}</span>
                  {section && showProgress && (
                    <span
                      data-testid={`progress-dot-${tab.id}`}
                      title={`${section.done} of ${section.total} required fields`}
                      className={`w-2 h-2 rounded-full flex-shrink-0 transition-colors ${
                        section.complete
                          ? 'bg-green-500'
                          : section.untouched
                          ? 'bg-gray-300'
                          : 'bg-blue-500'
                      }`}
                    />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </nav>

      {showProgress && (
        <ProjectProgressBar progress={progress} onNavigate={(tab) => setActiveTab(tab)} />
      )}
      </div>

      {inDemo && (
        <div
          data-testid="demo-banner"
          className="bg-blue-600 text-white px-4 py-2 flex items-center justify-center gap-2"
        >
          <Sparkles className="w-4 h-4 flex-shrink-0" />
          <p className="text-xs sm:text-sm font-medium">
            Demo — sample job with sample data. Nothing here is saved, and your own projects are untouched.
          </p>
        </div>
      )}

      {enterpriseSuccessBanner && (
        <div className="bg-green-50 border-b border-green-200 px-4 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="bg-green-600 text-white rounded-full p-1.5 flex-shrink-0">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
            </div>
            <div>
              <p className="text-sm font-semibold text-green-900">Welcome to Enterprise!</p>
              <p className="text-xs text-green-800">Your firm account is ready. An admin dashboard is coming soon — for now, you can manage your subscription in Account Settings.</p>
            </div>
          </div>
          <button
            onClick={() => setEnterpriseSuccessBanner(false)}
            className="text-green-700 hover:text-green-900 text-xl leading-none flex-shrink-0"
            aria-label="Dismiss"
          >
            &times;
          </button>
        </div>
      )}

      {isFirmViewOnly && (
        <div className="bg-blue-50 border-b border-blue-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
            <div className="flex items-center space-x-3">
              <Users className="w-5 h-5 text-blue-700" />
              <p className="text-sm font-medium text-blue-900">
                Viewing {firmProjectOwnerName ? `${firmProjectOwnerName}’s project` : "a firm member’s project"} — read only. You can generate reports but cannot edit this project.
              </p>
            </div>
          </div>
        </div>
      )}

      {isReadOnly && !isFirmViewOnly && (
        <div className="bg-orange-100 border-b border-orange-300">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Lock className="w-5 h-5 text-orange-700" />
                <div>
                  <p className="text-sm font-medium text-orange-900">
                    Read-Only Mode: This project has been completed
                  </p>
                  <p className="text-xs text-orange-800">
                    All data is locked to prevent accidental modifications
                  </p>
                </div>
              </div>
              {!isFirmViewOnly && (
                <button
                  onClick={() => setShowUnlockDialog(true)}
                  className="flex items-center space-x-2 px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 transition-colors text-sm font-medium"
                >
                  <Unlock className="w-4 h-4" />
                  <span>Unlock Project</span>
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      <main className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8">
        {activeTab === 'project' && (
          <ProjectInfoForm
            project={project}
            onChange={isFirmViewOnly ? noopHandler : handleProjectChange}
            isReadOnly={isReadOnly || isFirmViewOnly}
            showRequiredHighlights={showRequiredHighlights}
            onEditProfile={() => {
              setAccountSettingsTab('company');
              setShowAccountSettings(true);
            }}
          />
        )}
        {activeTab === 'service' && (
          <ServiceInfoForm
            project={project}
            isReadOnly={isReadOnly || isFirmViewOnly}
            onChange={isFirmViewOnly ? noopHandler : handleProjectChange}
            showRequiredHighlights={showRequiredHighlights}
          />
        )}
        {activeTab === 'ami' && (
          <ExistingLoadStep
            key={projectId ?? 'no-project'}
            projectId={projectId}
            project={project}
            amiDataCount={amiData.length}
            amiData={amiData}
            equipment={equipment}
            onChange={isFirmViewOnly ? noopHandler : handleProjectChange}
            onUploadCSV={isFirmViewOnly ? noopHandler : handleUploadCSV}
            onDeleteData={isFirmViewOnly ? noopHandler : handleDeleteAMIData}
            onAddExisting={isFirmViewOnly ? noopHandler : handleAddExistingLoad}
            onSplitExisting={isFirmViewOnly ? noopHandler : handleSplitExistingLoad}
            onMarkRemoved={isFirmViewOnly ? undefined : handleMarkRemoved}
            onReturnToInventory={isFirmViewOnly ? undefined : handleReturnToInventory}
            onUpdateEquipment={isFirmViewOnly ? noopHandler : handleUpdateEquipment}
            onDeleteEquipment={isFirmViewOnly ? noopHandler : handleDeleteEquipment}
            isReadOnly={isReadOnly || isFirmViewOnly}
            showRequiredHighlights={showRequiredHighlights}
            subscriptionStatus={paymentStatus.subscription_status}
            unlocked={isResultsUnlocked(project, paymentStatus)}
            onRelockTriggered={isFirmViewOnly ? undefined : () => {
              handleProjectChange({ results_unlocked: false, results_unlocked_at: null });
              setPaymentStatusKey(k => k + 1);
            }}
            mode={existingLoadMode}
            onModeChange={setExistingLoadMode}
            onReloadProject={isFirmViewOnly ? undefined : handleReloadProjectFromCloud}
            onNavigate={(tab) => setActiveTab(tab)}
          />
        )}
        {activeTab === 'equipment' && (
          <EquipmentSchedule
            equipment={proposedLoads(equipment)}
            onAdd={isFirmViewOnly ? noopHandler : handleAddEquipment}
            onUpdate={isFirmViewOnly ? noopHandler : handleUpdateEquipment}
            onDelete={isFirmViewOnly ? noopHandler : handleDeleteEquipment}
            isReadOnly={isReadOnly || isFirmViewOnly}
            peakSeason={determineSeason(project.peak_timestamp ?? null)}
            measuredCitation={citationsFor(project).measured}
            recent={recentEquipment}
            onRemoveRecent={removeRecentEquipment}
            askBackupHeat={resolvedEditionKey(project) === '2026'}
          />
        )}
        {activeTab === 'equipment' && (
          <div className="mt-6">
            <EquipmentSchedule
              variant="removed"
              equipment={removedLoads(equipment)}
              onAdd={isFirmViewOnly ? noopHandler : handleAddRemovedLoad}
              onUpdate={isFirmViewOnly ? noopHandler : handleUpdateEquipment}
              onDelete={isFirmViewOnly ? noopHandler : handleDeleteEquipment}
              isReadOnly={isReadOnly || isFirmViewOnly}
              peakSeason={determineSeason(project.peak_timestamp ?? null)}
              measuredCitation={citationsFor(project).measured}
              removedNote={removedTreatmentNote}
              onReturnToInventory={
                isFirmViewOnly ||
                (existingLoads(equipment).length === 0 && project.dwelling_floor_area_sqft == null)
                  ? undefined
                  : handleReturnToInventory
              }
            />
          </div>
        )}
        {activeTab === 'results' && (
          <ResultsView
            project={project}
            equipment={equipment}
            amiData={amiInEffect}
            projectId={projectId}
            paymentStatus={paymentStatus}
            onMarkComplete={isFirmViewOnly || inDemo ? undefined : handleInitiateComplete}
            onChange={isFirmViewOnly ? undefined : handleProjectChange}
            onPaymentStatusRefresh={() => setPaymentStatusKey(k => k + 1)}
            onEnsureSaved={handleSave}
            onNavigate={(tab) => setActiveTab(tab)}
            firmLogoSrc={firmLogoSrc}
          />
        )}
        {activeTab === 'help' && <HelpTab onStartDemo={() => void startDemo()} />}
      </main>

      <footer className="bg-white border-t border-gray-200 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-600">
              <p>
                This tool provides a service adequacy evaluation aid. Final
                responsibility for code compliance remains with the licensed
                preparer and Authority Having Jurisdiction.
              </p>
            </div>
          </div>
        </div>
      </footer>

      {showWelcomeModal && (
        <WelcomeModal
          userName={userProfile?.full_name}
          onWatchDemo={async () => {
            setShowWelcomeModal(false);
            await startDemo();
          }}
          onSkip={() => {
            setShowWelcomeModal(false);
            void setOnboardingStatus('declined');
            setShowProjectNameDialog(true);
          }}
          onOpenExisting={() => {
            setShowWelcomeModal(false);
            setShowProjectManager(true);
          }}
        />
      )}

      {demoSeed && (
        <DemoOverlay
          seed={demoSeed}
          onPatchProject={handleProjectChange}
          onAddEquipment={handleDemoAddEquipment}
          onUpdateEquipment={handleDemoUpdateEquipment}
          onSeedAmi={handleDemoSeedAmi}
          onAddExisting={handleDemoAddExisting}
          onSetMode={setExistingLoadMode}
          existingLoadMode={existingLoadMode}
          onApplyState={handleDemoApplyState}
          onNavigateTab={(tab) => setActiveTab(tab)}
          onExit={() => endDemo(false)}
          onComplete={() => endDemo(true)}
        />
      )}

      {showProjectManager && (
        <ProjectManager
          currentProjectId={projectId}
          onLoadProject={handleLoadProject}
          onLoadFirmProject={handleLoadFirmProject}
          onNewProject={handleNewProject}
          onActiveProjectDeleted={handleActiveProjectDeleted}
          onClose={() => {
            setShowProjectManager(false);
            // If no project is active, prompt the user to create one
            // rather than leaving them on a blank screen.
            if (!projectId) setShowProjectNameDialog(true);
          }}
        />
      )}

      {showProjectNameDialog && (
        <ProjectNameDialog
          onConfirm={handleCreateProject}
          onCancel={() => setShowProjectNameDialog(false)}
        />
      )}

      {showUnlockDialog && (
        <UnlockProjectDialog
          projectName={project.project_name || 'Untitled Project'}
          onConfirm={handleUnlockProject}
          onCancel={() => setShowUnlockDialog(false)}
        />
      )}

      {showAccountSettings && (
        <AccountSettings initialTab={accountSettingsTab} onClose={() => setShowAccountSettings(false)} />
      )}

      {pendingCheckout?.kind === 'unlimited' && (
        <BillingConsentModal
          kind="subscription"
          planLabel="Unlimited"
          amountText="$59.00"
          intervalText="month"
          onConfirm={async () => {
            setPendingCheckout(null);
            try {
              const { data: { session } } = await supabase.auth.getSession();
              if (!session) return;
              const res = await fetch('/.netlify/functions/create-checkout-session', {
                method: 'POST',
                headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ type: 'subscription' }),
              });
              if (res.ok) {
                const data = await res.json();
                if (data.url) window.location.href = data.url;
              }
            } catch { /* user lands in app normally */ }
          }}
          onCancel={() => setPendingCheckout(null)}
        />
      )}

      {pendingCheckout?.kind === 'enterprise' && (() => {
        const { company_name, seat_count, billing_cycle, take_seat } = pendingCheckout;
        const total = enterpriseTotal(seat_count, billing_cycle);
        const cadence = billing_cycle === 'monthly' ? 'month' : 'year';
        return (
          <BillingConsentModal
            kind="subscription"
            planLabel="Enterprise"
            amountText={`$${total.toFixed(2)}`}
            intervalText={cadence}
            onConfirm={async () => {
              setPendingCheckout(null);
              try {
                const { data: { session } } = await supabase.auth.getSession();
                if (!session) return;
                const res = await fetch('/.netlify/functions/create-enterprise-checkout-session', {
                  method: 'POST',
                  headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
                  body: JSON.stringify({ company_name, seat_count, billing_cycle, take_seat }),
                });
                if (res.ok) {
                  const data = await res.json();
                  if (data.url) window.location.href = data.url;
                }
              } catch { /* user lands in app normally */ }
            }}
            onCancel={() => setPendingCheckout(null)}
          />
        );
      })()}

      {showValidationModal && validationResult && (
        <CompletionValidationModal
          result={validationResult}
          onClose={() => setShowValidationModal(false)}
          onComplete={(warningAcknowledged) => handleMarkComplete(warningAcknowledged)}
          onNavigate={(tab) => {
            setShowValidationModal(false);
            // CompletionValidationModal's Tab union includes 'report', which
            // this app has no tab for. FIELD_TO_TAB in
            // projectCompletionValidation.ts only ever produces
            // 'project' | 'service' | 'equipment' | 'ami', so this branch is
            // unreachable; it exists to narrow the union to App's Tab type.
            if (tab === 'report') return;
            setActiveTab(tab);
          }}
        />
      )}

      {autoSaveError && (
        <div className="fixed bottom-4 right-4 z-50 flex items-center gap-2 px-4 py-3 bg-red-600 text-white rounded-lg shadow-lg text-sm font-medium">
          <AlertCircle size={16} className="flex-shrink-0" />
          {autoSaveError}
        </div>
      )}

      {paywallToast && (
        <div className="fixed bottom-4 right-4 z-50 flex items-center gap-2 px-4 py-3 bg-green-600 text-white rounded-lg shadow-lg text-sm font-medium">
          <CheckCircle size={16} className="flex-shrink-0" />
          {paywallToast}
        </div>
      )}

      <AmiPersistStatus />
    </div>
  );
}

export default App;
